"use client";

import { motion, AnimatePresence } from 'motion/react';
import { Github, Linkedin, Mail, ChevronDown, ArrowRight, Minus } from 'lucide-react';
import Link from 'next/link';
import React, { useState } from 'react';

import { cn } from '@/core/utils/utils';
import { serviceCategories, subServices } from '@/data/services';


export function FluidFooter() {
    const [isServicesOpen, setIsServicesOpen] = useState(false);

    return (
        <footer className="relative bg-background border-t border-border overflow-hidden transition-colors duration-300">

            {/* Footer Navigation (Standard Overlay) */}
            <div className="container mx-auto px-4 py-8 relative z-20 border-t border-border/50 bg-background/80 backdrop-blur-xs transition-colors duration-300">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="text-center md:text-left space-y-1">
                        <p className="text-sm font-medium text-foreground">Hamid Ayub</p>
                        <p className="text-xs text-muted-foreground">
                            Building what matters. Shipping what scales.
                        </p>
                    </div>

                    <nav className="flex items-center gap-6">
                        {[
                            { name: "Work", href: "/work" },
                            { name: "Services", href: "/services" },
                            { name: "About", href: "/about" },
                            { name: "Blog", href: "/blog" },
                            { name: "Writing", href: "/writing" },
                            { name: "Contact", href: "/contact" },
                        ].map((link) => (
                            <Link
                                key={link.href}
                                href={link.href}
                                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                            >
                                {link.name}
                            </Link>
                        ))}
                    </nav>

                    <div className="flex items-center gap-4">
                        <a href="mailto:contact@hamidayub.com" aria-label="Email Contact" className="text-muted-foreground hover:text-foreground transition-colors">
                            <Mail className="w-4 h-4" />
                        </a>
                        <a href="https://github.com/Hamid-Ayub" target="_blank" rel="noopener noreferrer" aria-label="GitHub Profile" className="text-muted-foreground hover:text-foreground transition-colors">
                            <Github className="w-4 h-4" />
                        </a>
                        <a href="https://www.linkedin.com/in/hamid-ayub/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn Profile" className="text-muted-foreground hover:text-foreground transition-colors">
                            <Linkedin className="w-4 h-4" />
                        </a>
                    </div>
                </div>

                {/* Expandable Services Section */}
                <div className="mt-8 border-t border-border/50 pt-6">
                    <button
                        onClick={() => setIsServicesOpen(!isServicesOpen)}
                        className="group flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors mx-auto md:mx-0"
                    >
                        <span>Explore Services</span>
                        <ChevronDown className={cn("w-3 h-3 transition-transform duration-300 group-hover:translate-y-0.5", isServicesOpen ? "rotate-180" : "")} />
                    </button>

                    <AnimatePresence>
                        {isServicesOpen && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} // Bezier for smooth premium feel
                                className="overflow-hidden"
                            >
                                <div className="mt-8 pt-8 pb-12 border-t border-border/50 bg-linear-to-b from-foreground/2 to-transparent rounded-lg relative">
                                    {/* Glassy Overlay */}
                                    <div className="absolute inset-0 bg-background/20 backdrop-blur-[2px] pointer-events-none" />

                                    <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 px-8 divide-y md:divide-y-0 md:divide-x divide-border/50">
                                        {serviceCategories.map((category) => (
                                            <div key={category.id} className="py-6 md:py-0 md:px-8 first:pl-0 last:pr-0">
                                                <Link
                                                    href={`/services/${category.id}`}
                                                    className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors mb-6 group"
                                                >
                                                    <Minus className="w-3 h-3 text-muted-foreground group-hover:text-primary transition-colors" />
                                                    {category.translations.en.title}
                                                </Link>

                                                <ul className="space-y-3">
                                                    {subServices[category.id as keyof typeof subServices]?.map((service) => (
                                                        <li key={service.id}>
                                                            <Link
                                                                href={`/services/${category.id}/${service.id}`}
                                                                className="group flex items-center justify-between text-sm text-muted-foreground hover:text-foreground transition-colors"
                                                            >
                                                                <span className="relative">
                                                                    {service.translations.en.title}
                                                                    <span className="absolute -bottom-1 left-0 w-0 h-px bg-primary transition-all duration-300 group-hover:w-full opacity-50" />
                                                                </span>
                                                                <ArrowRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 text-primary" />
                                                            </Link>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                <div className="text-center mt-8 pt-6 border-t border-border/50 flex flex-col md:flex-row justify-between items-center gap-4">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        © {new Date().getFullYear()} Hamid Ayub.
                    </p>
                    <div className="flex gap-4 text-[10px] uppercase tracking-wider text-muted-foreground">
                        <Link href="/privacy-policy" className="hover:text-foreground transition-colors">Privacy</Link>
                        <Link href="/terms-of-service" className="hover:text-foreground transition-colors">Terms</Link>
                        <Link href="/refund-policy" className="hover:text-foreground transition-colors">Refund Policy</Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}

